import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from "@mui/joy";
import { Link } from "react-router-dom";
import { apiClient } from "../../../lib/api";
import { ResponsiveTable } from "../../../components/common/ResponsiveTable";
import type {
  SalesCommand,
  SalesDeskData,
  SalesDispatch,
  SalesPlan,
  SalesPlaybook,
  SalesPlaybooks,
  SalesRun,
} from "../../../types/salesDesk";
import { base, load, stamp, stops, stopLabel } from "./shared";

export function SalesPlaybooksPanel({
  data,
  command,
  busy,
}: {
  data: SalesDeskData;
  command: SalesCommand;
  busy: boolean;
}) {
  const query = useQuery({
    queryKey: ["sales-desk", "playbooks"],
    queryFn: () => load<SalesPlaybooks>(base + "playbooks/"),
    refetchInterval: 15000,
  });
  const [create, setCreate] = useState(false),
    [book, setBook] = useState<SalesPlaybook | null>(null),
    [review, setReview] = useState<SalesDispatch | null>(null),
    [cancel, setCancel] = useState<SalesRun | null>(null),
    [reason, setReason] = useState(""),
    [error, setError] = useState("");
  const stopRun = async () => {
    try {
      await command("stop_playbook", { execution: cancel?.id, note: reason });
      setCancel(null);
      setReason("");
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        gap={2}
        justifyContent="space-between"
      >
        <Box>
          <Typography level="h3">
            Follow up with a clear stopping point
          </Typography>
          <Typography level="body-sm">
            Preview a roofing playbook, then choose each contact to enroll.
            Task-only playbooks need no email connection.
          </Typography>
        </Box>
        {data.permissions.create_playbooks && (
          <Button onClick={() => setCreate(true)}>Create playbook</Button>
        )}
      </Stack>
      <Alert color="neutral">
        Pending actions stop on customer replies, bookings, signatures,
        opt-outs, or a closed opportunity. Already completed tasks and messages
        remain in history.
      </Alert>
      {query.isPending && <CircularProgress />}
      {query.isError && <Alert color="danger">{query.error.message}</Alert>}
      {query.data && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", lg: "repeat(2,minmax(0,1fr))" },
              gap: 2,
            }}
          >
            {query.data.playbooks.map((b) => (
              <Card key={b.id}>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  <Typography level="title-lg">{b.name}</Typography>
                  <Chip size="sm" color={b.active ? "success" : "neutral"}>
                    {b.active ? "Active" : "Inactive"} · manual enrollment
                  </Chip>
                </Stack>
                <Typography level="body-sm">
                  {b.steps
                    .map((s) =>
                      s.kind === "wait"
                        ? `Wait ${s.config.duration_value} ${s.config.duration_unit}`
                        : s.kind === "send_email"
                          ? "Send reviewed email"
                          : "Create follow-up task",
                    )
                    .join(" → ")}
                </Typography>
                <Typography level="body-xs">
                  Stops: {b.stops.map((s) => stopLabel[s] || s).join(", ")}
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {data.permissions.manage_playbooks && (
                    <Button disabled={!b.active} onClick={() => setBook(b)}>
                      Preview & enroll
                    </Button>
                  )}
                  <Button
                    component={Link}
                    to="/advanced/automations"
                    variant="plain"
                  >
                    Edit in Automations
                  </Button>
                </Stack>
              </Card>
            ))}
          </Box>
          {query.data.playbooks.length === 0 && (
            <Card>
              <Typography>
                No playbooks yet. Start with inspection, proposal, or storm
                follow-up.
              </Typography>
            </Card>
          )}
          <Typography level="title-lg">Recent runs</Typography>
          <Card sx={{ p: { xs: 1, sm: 2 } }}>
            <ResponsiveTable
              rows={query.data.runs}
              getRowKey={(r) => r.id}
              pagination={{ pageSize: 10, itemNoun: "runs" }}
              emptyState="No contacts enrolled yet."
              columns={[
                {
                  key: "name",
                  header: "Playbook",
                  mobileVariant: "header",
                  render: (r) => (
                    <Box>
                      <Typography level="title-md">{r.name}</Typography>
                      <Typography
                        component={Link}
                        to={`/crm/contacts/${r.contact}`}
                        level="body-sm"
                      >
                        Contact #{r.contact}
                      </Typography>
                    </Box>
                  ),
                },
                {
                  key: "state",
                  header: "State",
                  render: (r) => (
                    <Chip
                      size="sm"
                      color={
                        r.status === "stopped"
                          ? "neutral"
                          : r.status === "user_intervention" ||
                              r.status === "failed"
                            ? "warning"
                            : "primary"
                      }
                    >
                      {r.status.replaceAll("_", " ")}
                    </Chip>
                  ),
                },
                {
                  key: "next",
                  header: "Next / outcome",
                  mobileVariant: "body",
                  render: (r) => (
                    <Box>
                      <Typography level="body-sm">
                        {r.stop?.reason ||
                          r.error ||
                          (r.resume_at
                            ? `Next step ${stamp(r.resume_at)}`
                            : "No pending wait")}
                      </Typography>
                      {r.results
                        .filter((v) => v.task_id)
                        .map((v, i) => (
                          <Typography
                            key={i}
                            component={Link}
                            to={`/operations/tasks?task=${v.task_id}`}
                            level="body-sm"
                            sx={{ display: "block" }}
                          >
                            Open created task #{v.task_id}
                          </Typography>
                        ))}
                    </Box>
                  ),
                },
              ]}
              rowActions={(r) =>
                data.permissions.manage_playbooks &&
                ["running", "waiting", "user_intervention"].includes(
                  r.status,
                ) ? (
                  <Button
                    size="sm"
                    variant="plain"
                    color="danger"
                    onClick={() => {
                      setCancel(r);
                      setReason("");
                      setError("");
                    }}
                  >
                    Stop
                  </Button>
                ) : null
              }
            />
          </Card>
          <Typography level="body-xs">
            Showing up to 100 recent runs for contacts you can access. Full
            execution history is available in Automations.
          </Typography>
          {query.data.dispatches.length > 0 && (
            <>
              <Typography level="title-lg">Mailbox handoffs</Typography>
              <Card sx={{ p: { xs: 1, sm: 2 } }}>
                <ResponsiveTable
                  rows={query.data.dispatches}
                  getRowKey={(r) => r.id}
                  columns={[
                    {
                      key: "subject",
                      header: "Message",
                      mobileVariant: "header",
                      render: (d) => (
                        <Box>
                          <Typography level="title-md">{d.subject}</Typography>
                          <Typography level="body-xs">
                            {d.sender} → {d.recipient}
                          </Typography>
                        </Box>
                      ),
                    },
                    {
                      key: "state",
                      header: "Outcome",
                      mobileVariant: "body",
                      render: (d) => (
                        <Box>
                          <Typography level="body-sm">
                            {d.state.replaceAll("_", " ")}
                          </Typography>
                          <Typography level="body-xs">
                            {d.error ||
                              d.review_note ||
                              "Accepted by the provider; delivery is tracked separately."}
                          </Typography>
                        </Box>
                      ),
                    },
                  ]}
                  rowActions={(d) =>
                    data.permissions.manage_playbooks &&
                    ["uncertain", "dispatching"].includes(d.state) ? (
                      <Button
                        size="sm"
                        variant="outlined"
                        onClick={() => setReview(d)}
                      >
                        Review outcome
                      </Button>
                    ) : null
                  }
                />
              </Card>
            </>
          )}
          {data.permissions.drips && query.data.drips.length > 0 && (
            <>
              <Divider />
              <Typography level="title-lg">
                Customer-event stops for existing email drips
              </Typography>
              <Typography level="body-sm">
                Enable these only for sales follow-ups that should end after a
                response. Existing campaign content and enrollment stay in Email
                Marketing.
              </Typography>
              {query.data.drips.map((drip) => (
                <DripPolicy
                  key={drip.id + JSON.stringify(drip.stop_on_customer_events)}
                  drip={drip}
                  command={command}
                  busy={busy}
                />
              ))}
            </>
          )}
        </>
      )}
      <Modal open={create} onClose={() => setCreate(false)}>
        <ModalDialog
          sx={{
            width: 680,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <ModalClose />
          <CreatePlaybook
            command={command}
            busy={busy}
            done={() => setCreate(false)}
          />
        </ModalDialog>
      </Modal>
      <Modal open={!!book} onClose={() => setBook(null)}>
        <ModalDialog
          sx={{
            width: 680,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <ModalClose />
          {book && (
            <Enroll
              book={book}
              command={command}
              busy={busy}
              done={() => setBook(null)}
            />
          )}
        </ModalDialog>
      </Modal>
      <Modal open={!!review} onClose={() => setReview(null)}>
        <ModalDialog
          sx={{
            width: 600,
            maxWidth: "calc(100vw - 24px)",
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          <ModalClose />
          {review && (
            <ReviewDispatch
              dispatch={review}
              command={command}
              busy={busy}
              done={() => setReview(null)}
            />
          )}
        </ModalDialog>
      </Modal>
      <Modal open={!!cancel} onClose={() => setCancel(null)}>
        <ModalDialog sx={{ width: 500, maxWidth: "calc(100vw - 24px)" }}>
          <ModalClose />
          <Typography level="h3">Stop pending follow-up</Typography>
          <Typography>Completed actions remain in history.</Typography>
          <FormControl>
            <FormLabel>Reason for stopping</FormLabel>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </FormControl>
          {error && <Alert color="danger">{error}</Alert>}
          <Button
            loading={busy}
            disabled={!reason.trim()}
            color="danger"
            onClick={() => void stopRun()}
          >
            Confirm stop
          </Button>
        </ModalDialog>
      </Modal>
    </Stack>
  );
}
function CreatePlaybook({
  command,
  busy,
  done,
}: {
  command: SalesCommand;
  busy: boolean;
  done: () => void;
}) {
  const [template, setTemplate] = useState("inspection"),
    [email, setEmail] = useState(false),
    [subject, setSubject] = useState(""),
    [body, setBody] = useState(""),
    [plan, setPlan] = useState<SalesPlan | null>(null),
    [loading, setLoading] = useState(false),
    [error, setError] = useState("");
  const payload = { template, email, subject, body };
  const preview = async () => {
    setLoading(true);
    setError("");
    try {
      setPlan(
        (await apiClient.put<SalesPlan>(base + "playbooks/", payload)).data,
      );
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  };
  const save = async () => {
    try {
      await command("create_playbook", payload);
      done();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Stack spacing={2}>
      <Typography level="h3" sx={{ pr: 4 }}>
        Create a roofing playbook
      </Typography>
      <FormControl>
        <FormLabel>Roofing workflow</FormLabel>
        <Select
          value={template}
          onChange={(_, v) => {
            setTemplate(v || "inspection");
            setPlan(null);
          }}
        >
          <Option value="inspection">Inspection follow-up</Option>
          <Option value="proposal">Proposal follow-up</Option>
          <Option value="storm">Storm lead follow-up</Option>
        </Select>
      </FormControl>
      <Checkbox
        checked={email}
        onChange={(e) => {
          setEmail(e.target.checked);
          setPlan(null);
        }}
        label="Include one automatic email after 24 hours"
      />
      {email && (
        <>
          <Alert>
            Uses the current assigned rep’s connected Profile mailbox. Nothing
            is sent when creating this playbook.
          </Alert>
          <FormControl>
            <FormLabel>Email subject</FormLabel>
            <Input
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value);
                setPlan(null);
              }}
            />
          </FormControl>
          <FormControl>
            <FormLabel>Email message</FormLabel>
            <Textarea
              minRows={4}
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                setPlan(null);
              }}
            />
          </FormControl>
        </>
      )}
      {error && <Alert color="danger">{error}</Alert>}
      <Button
        variant="outlined"
        loading={loading}
        onClick={() => void preview()}
      >
        Preview planned actions
      </Button>
      {plan && (
        <Card variant="soft">
          <Typography level="title-lg">{plan.name}</Typography>
          {plan.steps.map((s, i) => (
            <Typography key={i} level="body-sm">
              {i + 1}.{" "}
              {s.kind === "wait"
                ? `Wait ${s.hours} hours`
                : s.kind === "task"
                  ? `Task: ${s.title}`
                  : `Email: ${s.subject} · ${s.sender}`}
            </Typography>
          ))}
          <Typography level="body-sm">
            Stops on: {plan.stop_events.map((s) => stopLabel[s]).join(", ")}.
          </Typography>
          <Typography level="body-xs">
            Contacts are enrolled manually after creation.
          </Typography>
          <Button loading={busy} onClick={() => void save()}>
            Create reviewed playbook
          </Button>
        </Card>
      )}
    </Stack>
  );
}
function Enroll({
  book,
  command,
  busy,
  done,
}: {
  book: SalesPlaybook;
  command: SalesCommand;
  busy: boolean;
  done: () => void;
}) {
  const [search, setSearch] = useState(""),
    [contact, setContact] = useState(""),
    [error, setError] = useState("");
  const query = useQuery({
    queryKey: ["sales-desk", "enroll-contacts", search],
    queryFn: () =>
      load<SalesDeskData>(
        base + "?all=true&search=" + encodeURIComponent(search),
      ),
  });
  const save = async () => {
    try {
      await command("start_playbook", {
        contact: Number(contact),
        automation: book.id,
      });
      done();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const selected = query.data?.rows.find((r) => String(r.id) === contact);
  return (
    <Stack spacing={2}>
      <Typography level="h3" sx={{ pr: 4 }}>
        {book.name}
      </Typography>
      {book.steps.map((s) => (
        <Card key={s.order} size="sm">
          <Typography level="title-sm">
            {s.kind === "wait"
              ? `Wait ${s.config.duration_value} ${s.config.duration_unit}`
              : s.kind === "send_email"
                ? `Email: ${s.config.subject}`
                : String(s.config.title || s.config.name || "Task")}
          </Typography>
          {s.kind === "send_email" && (
            <Typography level="body-sm" sx={{ whiteSpace: "pre-wrap" }}>
              {String(s.config.body || "")}
            </Typography>
          )}
        </Card>
      ))}
      <Typography level="body-sm">
        Stop on {book.stops.map((s) => stopLabel[s]).join(", ")}.
      </Typography>
      <Input
        aria-label="Find contact for playbook"
        placeholder="Find a contact"
        value={search}
        onChange={(e) => {
          setSearch(e.target.value);
          setContact("");
        }}
      />
      <FormControl>
        <FormLabel>Contact to enroll</FormLabel>
        <Select
          value={contact}
          onChange={(_, v) => setContact(v || "")}
          placeholder="Choose a contact"
        >
          {query.data?.rows.map((c) => (
            <Option key={c.id} value={String(c.id)}>
              {c.name} · {c.owner}
            </Option>
          ))}
        </Select>
      </FormControl>
      {selected && (
        <Typography level="body-sm">
          Current owner: {selected.owner}. Tasks and any optional email use the
          owner assigned when that step executes.
        </Typography>
      )}
      {error && <Alert color="danger">{error}</Alert>}
      <Button loading={busy} disabled={!contact} onClick={() => void save()}>
        Start reviewed follow-up
      </Button>
    </Stack>
  );
}
function ReviewDispatch({
  dispatch,
  command,
  busy,
  done,
}: {
  dispatch: SalesDispatch;
  command: SalesCommand;
  busy: boolean;
  done: () => void;
}) {
  const [decision, setDecision] = useState("accepted_manually"),
    [note, setNote] = useState(""),
    [error, setError] = useState("");
  const save = async () => {
    try {
      await command("review_dispatch", {
        dispatch: dispatch.id,
        decision,
        note,
      });
      done();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Stack spacing={2}>
      <Typography level="h3" sx={{ pr: 4 }}>
        Review mailbox outcome
      </Typography>
      <Alert color="warning">
        Check {dispatch.sender} before deciding. An uncertain provider response
        may still have sent the message. Automatic retries are held to avoid
        duplicates.
      </Alert>
      <Typography>
        {dispatch.subject} → {dispatch.recipient}
      </Typography>
      <FormControl>
        <FormLabel>Confirmed outcome</FormLabel>
        <Select
          value={decision}
          onChange={(_, v) => setDecision(v || "accepted_manually")}
        >
          <Option value="accepted_manually">
            Confirmed sent — never resend this step
          </Option>
          <Option value="retry_allowed">
            Confirmed not sent — allow retry
          </Option>
        </Select>
      </FormControl>
      <FormControl>
        <FormLabel>Mailbox evidence and review note</FormLabel>
        <Textarea
          minRows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </FormControl>
      {error && <Alert color="danger">{error}</Alert>}
      <Button
        loading={busy}
        disabled={!note.trim()}
        onClick={() => void save()}
      >
        Save reviewed outcome
      </Button>
      <Typography level="body-sm">
        This records a staff review, not a delivery receipt. Continue an
        interrupted run from Automations after saving.
      </Typography>
      <Button component={Link} variant="plain" to="/advanced/automations">
        Open Automations
      </Button>
    </Stack>
  );
}
function DripPolicy({
  drip,
  command,
  busy,
}: {
  drip: SalesPlaybooks["drips"][number];
  command: SalesCommand;
  busy: boolean;
}) {
  const [events, setEvents] = useState(drip.stop_on_customer_events),
    [error, setError] = useState("");
  const save = async () => {
    try {
      await command("drip_stops", { campaign: drip.id, events });
      setError("");
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Card>
      <Typography level="title-md">{drip.name}</Typography>
      <Stack direction="row" gap={2} flexWrap="wrap">
        {stops.map((s) => (
          <Checkbox
            key={s}
            label={stopLabel[s]}
            checked={events.includes(s)}
            onChange={(e) =>
              setEvents(
                e.target.checked
                  ? [...events, s]
                  : events.filter((v) => v !== s),
              )
            }
          />
        ))}
      </Stack>
      {error && <Alert color="danger">{error}</Alert>}
      <Button variant="outlined" loading={busy} onClick={() => void save()}>
        Save customer-event stops
      </Button>
    </Card>
  );
}
